// ============================================
// FICHARAZONSOCIAL.JS — Detalle de razón social con datos fiscales editables
// ============================================

import React, { useEffect, useState } from 'react';
import PedirDatosCard from './PedirDatosCard';
import KitProveedorCard from './KitProveedorCard';
import GarantiasLista from './GarantiasLista';
import {
  API_BASE_CLI, PP, COLOR_PRINCIPAL, COLOR_OSCURO, COLOR_FONDO, COLOR_GRIS,
  COLOR_GRIS_CLARO, COLOR_BORDE, COLOR_ADVERTENCIA,
  estiloInput, estiloBotonPrimario, estiloBotonSecundario, estiloTarjeta,
  CabeceraFicha, PuntoEstado, faltantesRazonSocial,
} from './helpersClientes';
import { authHeaders } from '../utils/storage';
import FichaSucursal from './FichaSucursal';
import ContactosAlcance from './ContactosAlcance';
import DetectorDuplicados from './DetectorDuplicados';
import ModalFusion from './ModalFusion';
import BotonArchivarCliente from './BotonArchivarCliente';
import SelloAutoria from './SelloAutoria';
import ModalConvertirSucursal from './ModalConvertirSucursal';

const CAMPOS_FISCALES = [
  { k: 'razon_social',         label: 'Razón social', requerido: true },
  { k: 'rfc',                  label: 'RFC' },
  { k: 'regimen_sat',          label: 'Régimen SAT' },
  { k: 'domicilio_fiscal',     label: 'Domicilio fiscal', multiline: true },
  { k: 'codigo_postal_fiscal', label: 'Código postal' },
  { k: 'email_facturacion',    label: 'Email facturación' },
  { k: 'metodo_pago_preferido', label: 'Método de pago' },
];

export default function FichaRazonSocial({ id, onVolver, esCelular }) {
  const [rs, setRs] = useState(null);
  const [sucursales, setSucursales] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [sucursalDetalle, setSucursalDetalle] = useState(null);
  const [form, setForm] = useState({});
  const [editando, setEditando] = useState(false);
  const [modalSucursal, setModalSucursal] = useState(false);
  const [modalRfcDuplicado, setModalRfcDuplicado] = useState(null); // razón social duplicada | null
  const [modalFusion, setModalFusion] = useState(null); // { absorbidaCompleta } | null
  // SPRINT SUCURSALES 1: modal de conversion individual empresa -> sucursal.
  const [modalConvertir, setModalConvertir] = useState(false);
  const [cargando, setCargando] = useState(true);

  const cargar = async () => {
    setCargando(true);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/clientes/razones-sociales/${id}`, { headers: authHeaders() }).then(x => x.json());
      if (r.ok) {
        setRs(r.razon_social);
        setSucursales(r.sucursales || []);
        setForm(r.razon_social);
      }
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, [id]);

  useEffect(() => {
    if (editando && grupos.length === 0) {
      fetch(`${API_BASE_CLI}/api/clientes/grupos`, { headers: authHeaders() }).then(x => x.json()).then(r => setGrupos(r.grupos || []));
    }
  }, [editando, grupos.length]);

  const guardar = async () => {
    // Si el usuario cambió/agregó RFC, verificar duplicados antes del PATCH
    const rfcNuevo = (form.rfc || '').replace(/\s+/g, '').toUpperCase();
    const rfcActual = (rs.rfc || '').replace(/\s+/g, '').toUpperCase();
    if (rfcNuevo && rfcNuevo !== rfcActual) {
      try {
        const dup = await fetch(
          `${API_BASE_CLI}/api/clientes/razones-sociales/buscar-por-rfc?rfc=${encodeURIComponent(rfcNuevo)}&excluir_id=${id}`,
          { headers: authHeaders() }
        ).then(x => x.json());
        if (dup.ok && (dup.razones_sociales || []).length > 0) {
          setModalRfcDuplicado(dup.razones_sociales[0]);
          return;
        }
      } catch { /* best-effort: sigue sin bloquear */ }
    }
    await persistir(form);
  };

  const persistir = async (datos) => {
    const r = await fetch(`${API_BASE_CLI}/api/clientes/razones-sociales/${id}`, {
      method: 'PATCH', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(datos),
    }).then(x => x.json());
    if (r.ok) {
      setRs(r.razon_social);
      setEditando(false);
    }
    cargar();
  };

  if (sucursalDetalle) {
    return <FichaSucursal id={sucursalDetalle} onVolver={() => { setSucursalDetalle(null); cargar(); }} esCelular={esCelular} />;
  }

  if (cargando || !rs) {
    return <div style={{ fontFamily: PP, padding: 40, textAlign: 'center', color: COLOR_GRIS_CLARO }}>Cargando razón social…</div>;
  }

  const faltantes = faltantesRazonSocial(rs);

  return (
    <div style={{ fontFamily: PP, background: COLOR_FONDO, minHeight: '100%', color: COLOR_GRIS }}>
      <CabeceraFicha
        volver={onVolver}
        titulo={rs.razon_social}
        subtitulo={`Razón social${rs.grupo_nombre ? ` · Grupo (cliente): ${rs.grupo_nombre}` : ' · Sin grupo (cliente)'} · ${sucursales.length} sucursal${sucursales.length === 1 ? '' : 'es'}`}
        estado={rs.estado}
        acciones={
          editando
            ? (
              <>
                <button style={estiloBotonSecundario} onClick={() => { setEditando(false); setForm(rs); }}>Cancelar</button>
                <button style={{ ...estiloBotonPrimario, marginLeft: 8 }} onClick={guardar}>Guardar</button>
              </>
            )
            : (
              <>
                <button
                  style={{ ...estiloBotonSecundario, minHeight: 44 }}
                  onClick={() => setEditando(true)}
                >Editar datos fiscales</button>
                {rs.grupo_id != null && rs.estado !== 'archivado' && (
                  /* SPRINT SUCURSALES 1: conversion individual (mismo modal del lote). */
                  <button
                    style={{ ...estiloBotonSecundario, marginLeft: 8, minHeight: 44 }}
                    onClick={() => setModalConvertir(true)}
                  >🏬 Convertir en sucursal</button>
                )}
              </>
            )
        }
      />

      {modalConvertir && (
        <ModalConvertirSucursal
          empresas={[{ id: rs.id, nombre: rs.razon_social, grupoId: rs.grupo_id, grupoNombre: rs.grupo_nombre }]}
          onCerrar={() => setModalConvertir(false)}
          onConvertidas={() => { setModalConvertir(false); onVolver(); }}
        />
      )}

      <div style={{ padding: esCelular ? 16 : 24, display: 'grid', gap: 16 }}>
        {/* Datos fiscales */}
        <div style={estiloTarjeta}>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 14 }}>Datos fiscales</div>
          {!editando && faltantes.length > 0 && (
            <div style={{
              padding: 10, background: '#fff4e5', borderRadius: 8, marginBottom: 14,
              fontSize: 12, color: '#a86b1e',
            }}>
              ⚠️ Faltan datos para poder facturar: <strong>{faltantes.join(', ')}</strong>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr', gap: 14 }}>
            {CAMPOS_FISCALES.map(({ k, label, multiline, requerido }) => (
              <div key={k} style={{ gridColumn: multiline ? (esCelular ? 'auto' : '1 / -1') : 'auto' }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: COLOR_GRIS_CLARO, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {label}
                  {!requerido && !rs[k] && !editando && (
                    <span title="Pendiente" style={{ color: COLOR_ADVERTENCIA }}>⚠️</span>
                  )}
                </label>
                {editando ? (
                  multiline ? (
                    <textarea
                      style={{ ...estiloInput, minHeight: 60, resize: 'vertical' }}
                      value={form[k] || ''}
                      onChange={(e) => setForm(f => ({ ...f, [k]: e.target.value }))}
                    />
                  ) : (
                    <input
                      style={estiloInput}
                      value={form[k] || ''}
                      onChange={(e) => setForm(f => ({ ...f, [k]: e.target.value }))}
                    />
                  )
                ) : (
                  <div style={{ fontSize: 13, color: rs[k] ? COLOR_GRIS : COLOR_GRIS_CLARO, marginTop: 4 }}>
                    {rs[k] || '— Sin especificar —'}
                  </div>
                )}
              </div>
            ))}

            {editando && (
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: COLOR_GRIS_CLARO }}>Grupo (cliente)</label>
                <select
                  style={estiloInput}
                  value={form.grupo_id || ''}
                  onChange={(e) => setForm(f => ({ ...f, grupo_id: e.target.value || null }))}
                >
                  <option value="">— Sin grupo (cliente) —</option>
                  {grupos.map(g => <option key={g.id} value={g.id}>{g.nombre}</option>)}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Sucursales */}
        <div style={estiloTarjeta}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO }}>Sucursales</div>
            <button style={estiloBotonPrimario} onClick={() => setModalSucursal(true)}>+ Sucursal</button>
          </div>
          {sucursales.length === 0 && (
            <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, padding: '8px 0' }}>Aún no hay sucursales.</div>
          )}
          <div style={{ display: 'grid', gap: 6 }}>
            {sucursales.map(s => (
              <div
                key={s.id}
                onClick={() => setSucursalDetalle(s.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                  background: COLOR_FONDO, borderRadius: 8, cursor: 'pointer',
                }}
              >
                <span style={{ fontSize: 16 }}>🏪</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: COLOR_OSCURO }}>{s.nombre_interno}</div>
                  <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 2 }}>
                    {[s.ciudad, s.direccion].filter(Boolean).join(' · ') || 'Sin dirección'}
                  </div>
                </div>
                <PuntoEstado estado={s.estado} />
              </div>
            ))}
          </div>
        </div>

        {/* Contactos nivel razón social */}
        <ContactosAlcance
          alcance_tipo="razon_social"
          alcance_id={id}
          titulo="Contactos de la razón social"
          tiposPermitidos={['administrador', 'gerente']}
        />
        {/* GARANTIAS (2026-09-07): certificados de este cliente, registro oficial. */}
        <div style={estiloTarjeta}>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 10 }}>Garantías</div>
          <GarantiasLista filtro={{ razon_social_id: id }} compacto />
        </div>
        {/* KIT DE PROVEEDOR (2026-09-08): lo que Beep entrega para darse de alta con este cliente. */}
        <KitProveedorCard cliente={{ razon_social_id: id, grupo_id: rs.grupo_id, nombre: rs.razon_social, correo: rs.email_facturacion || '' }} />
        {/* PORTAL DEL CLIENTE (2026-09-08): pedirle sus datos con un enlace temporal. */}
        <PedirDatosCard cliente={{ razon_social_id: id, grupo_id: rs.grupo_id, nombre: rs.razon_social }} />
      </div>

      {modalSucursal && (
        <ModalCrearSucursal
          razonSocialId={id}
          onCerrar={() => setModalSucursal(false)}
          onCreado={() => { setModalSucursal(false); cargar(); }}
          onUsarExistente={(item) => { setModalSucursal(false); setSucursalDetalle(item.id); }}
        />
      )}
      {modalRfcDuplicado && (
        <ModalRfcDuplicado
          duplicado={modalRfcDuplicado}
          rfcNuevo={form.rfc}
          onCerrar={() => setModalRfcDuplicado(null)}
          onFusionar={async () => {
            const dup = modalRfcDuplicado;
            setModalRfcDuplicado(null);
            // Cargar la razón social duplicada completa para el modal de fusión
            const full = await fetch(`${API_BASE_CLI}/api/clientes/razones-sociales/${dup.id}`, { headers: authHeaders() }).then(x => x.json());
            if (full.ok) setModalFusion({ conservada: full.razon_social, absorbida: rs });
          }}
          onCorregir={() => setModalRfcDuplicado(null)}
          onMantener={async () => {
            setModalRfcDuplicado(null);
            await persistir(form);
          }}
        />
      )}
      {modalFusion && (
        <ModalFusion
          tipo="razon_social"
          conservada={modalFusion.conservada}
          absorbida={modalFusion.absorbida}
          resumenConservada={`${modalFusion.conservada.rfc || 'sin RFC'}`}
          resumenAbsorbida={`${modalFusion.absorbida.rfc || 'sin RFC'} · ${sucursales.length} sucursal${sucursales.length === 1 ? '' : 'es'}`}
          onCerrar={() => setModalFusion(null)}
          onCompletado={() => {
            setModalFusion(null);
            // La absorbida (esta ficha) quedó archivada — volvemos a la lista
            onVolver();
          }}
        />
      )}

      {/* AUTORIA-CLIENTES (2026-09-18): quien lo creo y quien lo toco por ultima vez.
          Lo anterior a la migracion 087 sale como "desconocido": no se le atribuye a nadie. */}
      <div style={{ padding: esCelular ? '0 16px' : '0 24px' }}>
        <SelloAutoria entidad={rs} />
      </div>

      {/* Acciones admin discretas — solo Dirección puede archivar */}
      <div style={{ padding: esCelular ? '20px 16px 32px' : '24px 24px 40px', display: 'flex', justifyContent: 'flex-end' }}>
        <BotonArchivarCliente
          tipo="razon_social" id={rs.id} entidad={rs}
          onArchivado={onVolver}
        />
      </div>
    </div>
  );
}

function ModalRfcDuplicado({ duplicado, rfcNuevo, onCerrar, onFusionar, onCorregir, onMantener }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(26,58,92,0.5)', zIndex: 1100,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={onCerrar}>
      <div style={{
        background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 480,
        fontFamily: PP, color: COLOR_GRIS,
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#a86b1e', marginBottom: 14 }}>
          ⚠️ RFC ya registrado
        </div>
        <div style={{ fontSize: 13, color: COLOR_GRIS, marginBottom: 12 }}>
          El RFC <strong>{(rfcNuevo || '').toUpperCase()}</strong> ya está en:
        </div>
        <div style={{
          padding: 14, background: '#f3f4f4', borderRadius: 10, marginBottom: 16,
          border: `1px solid ${COLOR_BORDE}`,
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO }}>📄 {duplicado.razon_social}</div>
          {duplicado.grupo_nombre && (
            <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 4 }}>Grupo (cliente): {duplicado.grupo_nombre}</div>
          )}
          <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 4 }}>
            RFC: {duplicado.rfc} · {duplicado.sucursales_count || 0} sucursal{duplicado.sucursales_count === 1 ? '' : 'es'}
            {' · '}{duplicado.contactos_count || 0} contacto{duplicado.contactos_count === 1 ? '' : 's'}
          </div>
        </div>

        <div style={{ fontSize: 12, fontWeight: 600, color: COLOR_OSCURO, marginBottom: 8 }}>¿Qué quieres hacer?</div>
        <div style={{ display: 'grid', gap: 8 }}>
          <button
            onClick={onFusionar}
            style={{
              padding: 12, textAlign: 'left', border: `1px solid ${COLOR_BORDE}`,
              background: '#fff', borderRadius: 8, cursor: 'pointer', fontFamily: PP,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: COLOR_OSCURO }}>🔗 Fusionar con esta razón social</div>
            <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 2 }}>Consolidar todo en una sola</div>
          </button>
          <button
            onClick={onCorregir}
            style={{
              padding: 12, textAlign: 'left', border: `1px solid ${COLOR_BORDE}`,
              background: '#fff', borderRadius: 8, cursor: 'pointer', fontFamily: PP,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: COLOR_OSCURO }}>✏️ Verificar y corregir mi RFC</div>
            <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 2 }}>Volver a editar sin guardar</div>
          </button>
          <button
            onClick={onMantener}
            style={{
              padding: 12, textAlign: 'left', border: `1px solid ${COLOR_BORDE}`,
              background: '#fff', borderRadius: 8, cursor: 'pointer', fontFamily: PP,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: COLOR_OSCURO }}>🚫 Son distintas, mantener separadas</div>
            <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 2 }}>Caso raro, pero guardar igual</div>
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalCrearSucursal({ razonSocialId, onCerrar, onCreado, onUsarExistente }) {
  const [nombre, setNombre] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [similares, setSimilares] = useState(0);
  const crear = async () => {
    if (!nombre.trim()) return;
    setGuardando(true);
    try {
      await fetch(`${API_BASE_CLI}/api/clientes/sucursales`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ razon_social_id: razonSocialId, nombre_interno: nombre.trim() }),
      });
      onCreado();
    } finally {
      setGuardando(false);
    }
  };
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(26,58,92,0.45)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={onCerrar}>
      <div style={{
        background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 460, fontFamily: PP,
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 14 }}>Nueva sucursal</div>
        <label style={{ fontSize: 12, fontWeight: 600 }}>Nombre interno *</label>
        <input autoFocus style={estiloInput} value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Ej: Tlalpan" />
        <DetectorDuplicados
          tipo="sucursal"
          nombreActual={nombre}
          contextoId={razonSocialId}
          onSeleccionarExistente={onUsarExistente}
          onCambioResultados={setSimilares}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button style={estiloBotonSecundario} onClick={onCerrar}>Cancelar</button>
          <button style={estiloBotonPrimario} onClick={crear} disabled={!nombre.trim() || guardando}>
            {guardando ? 'Creando…' : (similares > 0 ? 'Sí, crear nueva sucursal' : 'Crear')}
          </button>
        </div>
      </div>
    </div>
  );
}
